/**
 * Local wall-clock formatting for backend LocalDate / LocalDateTime fields.
 *
 * `Date.toISOString()` renders the UTC wall clock with a `Z` suffix. The backend maps those
 * fields to LocalDateTime in the server zone, so a value built with `toISOString()` arrived one
 * or two hours early in Central Europe (recorded actual times, earliest starts, CSV dates,
 * availability windows; review round 5, findings 1.3, 1.4, 1.10 and 2.8). These helpers render
 * the local components instead and never append a zone.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

function requireValid(date: Date): void {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('Invalid time value');
  }
}

/** Local calendar day as `YYYY-MM-DD`. */
export function toLocalDateString(date: Date): string {
  requireValid(date);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local wall clock as `YYYY-MM-DDTHH:mm:ss`, the shape a backend LocalDateTime expects. */
export function toLocalDateTimeString(date: Date): string {
  requireValid(date);
  return `${toLocalDateString(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a date or timestamp string into a `Date` in the **local** zone.
 *
 * `new Date('2026-06-01')` is specified to read a date-only string as UTC midnight, while
 * `new Date('2026-06-01T00:00')` reads a date-time string without offset as local midnight.
 * Reading a date-only value with `new Date()` and then formatting its local components therefore
 * shifts it: `2026-06-01` becomes `2026-06-01T02:00:00` in Berlin and `2026-05-31T19:00:00` west
 * of UTC - an off-by-one day. CSV columns and run parameters carry exactly such date-only values,
 * so they are built from the components instead.
 *
 * Values with an explicit offset keep their meaning: they denote an instant, and converting it to
 * the local zone is the intended behaviour.
 */
export function parseLocalDateTime(value: string): Date {
  const match = DATE_ONLY.exec(value.trim());
  if (match) {
    const [year, month, day] = value.trim().split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}
